/**
 * Activity display names for the filing process UI.
 *
 * This module provides customer-facing labels for workflow activities. It is used by the API
 * (activityDisplayNameLoader) and consumed by the customer filing UI. Steps are hidden when
 * getActivityDisplayName returns null (e.g. internal cohort steps, evidence-collection steps).
 * In config, activityDisplayName: null means "use activityDescription as label"; only the
 * function's return value of null causes the step to be hidden (earlier rules can still hide).
 *
 * --- BEHAVIOR (getActivityDisplayName) ---
 *
 * Resolution order:
 *
 * 1. Presubmission → always "Verifying report data".
 *
 * 2. WaitForCohorts → cohort-specific message via getWaitForCohortsDisplayName(cohortTypes)
 *    (e.g. "Submitting all reports in jurisdiction together to local agencies (fire departments, LEPCs, etc)").
 *    Other cohort-only types (InitializeCohorts, ValidateCohort, ValidateCohortMembership,
 *    DistributeCohortDocuments) → null (hidden from customer).
 *
 * 3. CollectEvidence with description "Collect evidence: collectEvidenceOfCompletion" → null
 *    (internal step; hidden from customer).
 *
 * 4. FillPdf with description "Fill Certification" → null (hidden) except PA → "Generating certification letter".
 *
 * 5. Geppetto with description "" → state-based variant only (no state name in label). States in
 *    GEPPETTO_FEE_STATES show "Submitting report to state portal and paying fee";
 *    GEPPETTO_NOFEE_STATES show "Submitting report to state portal". Others hidden (null).
 *
 * 6. Declarative lookup by (state, type, description):
 *    - Key typeDescKey = `${activityType}-${activityDescription}`.
 *    - Key stateKey = `${state}-${typeDescKey}`.
 *    - Look up stateKey in activityDisplayStateOverrides, then typeDescKey in
 *      activityDisplayByTypeDesc (from ACTIVITY_DISPLAY_CONFIGS). State overrides win.
 *    - If a config entry has activityDisplayName === null: return activityDescription (show
 *      internal wording as label).
 *    - If a config entry has a string: interpolate [username] and [CN] in the string and return.
 *
 * 7. Fallback: getStaffFacingActivityTitle(activity, username, cn) — "Type - Description" with
 *    [username]/[CN] placeholders interpolated.
 *
 * --- IMPLEMENTATION ---
 *
 * - ACTIVITY_DISPLAY_CONFIGS: flat list of { activityType, activityDescription, activityDisplayName }.
 *   One entry per (type, description) that needs a customer label (or null to show description).
 * - ACTIVITY_DISPLAY_STATE_OVERRIDES: same shape plus `state`; only where the same (type,
 *   description) has a different displayName in that state (e.g. AL Lob "Mail to FD").
 * - GEPPETTO_FEE_STATES / GEPPETTO_NOFEE_STATES: arrays of state abbreviations; states in
 *   fee get "Submitting report to state portal and paying fee", nofee get "Submitting report to
 *   state portal". States not in either array are hidden (null).
 *
 * At module load, buildMapsFromDedupedConfigs() turns configs into:
 * - activityDisplayByTypeDesc: Map<typeDescKey, ActivityDisplayConfig>
 * - activityDisplayStateOverrides: Map<stateKey, ActivityDisplayConfig>
 *
 * Other exports: getStaffFacingActivityTitle (staff-style "Type - Description"), getActivityTitleOnly.
 */

import { ActivityType, CohortType } from "encamp-prisma/generated/browser";

import { prettyPrintEnumValue } from "../utils/prettyPrintEnumValue";

/** Only these cohort types are shown in the parenthetical (local agencies). Others (credential, state, alternate id) cause the step to be hidden when they are the only types. */
const LOCAL_AGENCY_COHORT_TYPES: readonly CohortType[] = [
  CohortType.FIRE_DEPARTMENT,
  CohortType.LEPC,
];

const LOCAL_AGENCY_DISPLAY_ORDER: CohortType[] = [CohortType.FIRE_DEPARTMENT, CohortType.LEPC];

function getLocalAgencyCohortLabel(type: CohortType): string {
  switch (type) {
    case CohortType.FIRE_DEPARTMENT:
      return "fire departments";
    case CohortType.LEPC:
      return "LEPCs";
    default:
      return prettyPrintEnumValue(type);
  }
}

/**
 * Build the customer-facing message for WaitForCohorts from the cohort types this activity waits for.
 * Only FIRE_DEPARTMENT and LEPC are shown in the parenthetical. If there are no local agency
 * cohort types (only credential, state, alternate id, or empty), returns null (step is hidden).
 * E.g. [FIRE_DEPARTMENT, LEPC] → "Submitting all reports in jurisdiction together to local agencies (fire departments, LEPCs, etc)"
 */
export function getWaitForCohortsDisplayName(cohortTypes: CohortType[]): string | null {
  const localAgencyTypes = cohortTypes.filter((t) => LOCAL_AGENCY_COHORT_TYPES.includes(t));
  if (localAgencyTypes.length === 0) {
    return null;
  }
  const base = "Submitting all reports in jurisdiction together to local agencies";
  const ordered = [...localAgencyTypes].sort(
    (a, b) => LOCAL_AGENCY_DISPLAY_ORDER.indexOf(a) - LOCAL_AGENCY_DISPLAY_ORDER.indexOf(b)
  );
  const labels = ordered.map(getLocalAgencyCohortLabel);
  const list = labels.join(", ");
  return `${base} (${list}, etc)`;
}

type ActivityDisplayConfig = {
  activityType: ActivityType;
  activityDescription: string;
  activityDisplayName: string | null;
};

/** Activity types that are internal cohort workflow steps; hidden from customer-facing UI (WaitForCohorts is shown with cohort-specific text) */
function isCohortOnlyActivityType(activityType: ActivityType): boolean {
  switch (activityType) {
    case ActivityType.InitializeCohorts:
    case ActivityType.ValidateCohort:
    case ActivityType.ValidateCohortMembership:
    case ActivityType.DistributeCohortDocuments:
      return true;
    default:
      return false;
  }
}

/**
 * Get Customer Facing Activity Display Name.
 * Returns null to hide the activity from the customer filing process UI (e.g. cohort-only steps).
 * WaitForCohorts is shown with a state/cohort-specific message when cohortTypes is provided.
 */
export function getActivityDisplayName({
  state,
  activityType,
  activityDescription,
  username,
  cn,
  cohortTypes,
}: {
  state: string;
  activityType: ActivityType;
  activityDescription: string;
  username?: string;
  cn?: string;
  cohortTypes?: CohortType[] | null;
}): string | null {
  if (activityType === ActivityType.Presubmission) {
    return "Verifying report data";
  }

  // WaitForCohorts: show cohort-specific message only when waiting for local agencies (FD/LEPC); otherwise hidden (null)
  if (activityType === ActivityType.WaitForCohorts) {
    return getWaitForCohortsDisplayName(cohortTypes ?? []);
  }

  // Cohort-only activities are internal workflow steps; hide from customer-facing UI
  if (isCohortOnlyActivityType(activityType)) {
    return null;
  }

  // Internal evidence-collection step; hide from customer UI
  if (
    activityType === ActivityType.CollectEvidence &&
    activityDescription === "Collect evidence: collectEvidenceOfCompletion"
  ) {
    return null;
  }

  // Fill Certification is internal; hide from customer UI except PA (explicit label there)
  if (activityType === ActivityType.FillPdf && activityDescription === "Fill Certification") {
    return state === "PA" ? "Generating certification letter" : null;
  }

  // Geppetto: "state portal" (no state name interpolation); fee = "and paying fee", nofee = portal only; else hidden
  if (activityType === ActivityType.Geppetto && activityDescription === "") {
    const variant = getGeppettoVariant(state);
    if (variant === null) return null;
    const base = "Submitting report to state portal";
    return variant === "fee" ? `${base} and paying fee` : base;
  }

  const typeDescKey = getTypeDescriptionKey(activityType, activityDescription);
  const stateKey = `${state}-${typeDescKey}`;
  const displayConfig =
    activityDisplayStateOverrides.get(stateKey) ?? activityDisplayByTypeDesc.get(typeDescKey);
  if (displayConfig) {
    if (displayConfig.activityDisplayName === null) {
      return activityDescription;
    }
    return interpolateDisplayName(displayConfig.activityDisplayName, {
      username,
      CN: cn,
    });
  }

  return getStaffFacingActivityTitle(
    {
      description: activityDescription,
      type: activityType,
    },
    username,
    cn
  );
}

function interpolateDisplayName(
  template: string,
  values: Record<string, string | undefined>
): string {
  return template.replace(/\[(\w+)]/g, (_, key) => {
    return values[key] ? `'${values[key]}'` : `[${key}]`; // fallback keeps placeholder if no value provided
  });
}

export function getStaffFacingActivityTitle(
  activity: {
    description?: string | null;
    type: ActivityType;
  },
  username?: string,
  cn?: string
): string {
  const displayName = interpolateDisplayName(activity.description ?? "", {
    username,
    CN: cn,
  });
  return [convertEnumToHumanReadable(activity.type), displayName].filter(Boolean).join(" - ");
}

// New function to get activity titles without descriptions (except for Staff activities)
export function getActivityTitleOnly(
  activity: {
    description?: string | null;
    type: ActivityType;
  },
  username?: string,
  cn?: string
): string {
  // For Staff activities, keep the full title with description
  if (activity.type === ActivityType.Staff) {
    return getStaffFacingActivityTitle(activity, username, cn);
  }

  // For all other activities, only show the activity type
  return convertEnumToHumanReadable(activity.type);
}

function convertEnumToHumanReadable(value: string): string {
  // Special cases for abbreviations
  const specialCases: Partial<Record<ActivityType, string>> = {
    WaitForAllLEPC: "Wait For All LEPC Reports",
    WaitForAllFD: "Wait For All FD Reports",
  };

  if (value in specialCases) {
    return specialCases[value as ActivityType] ?? value;
  }

  return prettyPrintEnumValue(value);
}

function getTypeDescriptionKey(activityType: ActivityType, activityDescription: string): string {
  return `${activityType}-${activityDescription}`;
}

/**
 * Deduplicated activity display config (type + description → displayName).
 * State-specific overrides and Geppetto by state are separate.
 */
const ACTIVITY_DISPLAY_CONFIGS: ActivityDisplayConfig[] = [
  {
    activityType: ActivityType.Tier2Submit,
    activityDescription: "",
    activityDisplayName: "Generating T2S report",
  },
  {
    activityType: ActivityType.Tier2SubmitValidation,
    activityDescription: "",
    activityDisplayName: null,
  },
  {
    activityType: ActivityType.SendSupplementalAgencyCommunication,
    activityDescription: "",
    activityDisplayName: "Send reports to any other required agencies",
  },
  {
    activityType: ActivityType.SendSupplementalAgencyCommunication,
    activityDescription: "Send communications to supplemental agencies",
    activityDisplayName: "Send reports to any other required agencies",
  },
  {
    activityType: ActivityType.GenerateCustomerDeliverables,
    activityDescription: "",
    activityDisplayName: "Filing complete",
  },
  {
    activityType: ActivityType.Lob,
    activityDescription: "Mail to LEPC",
    activityDisplayName: "Mailing report to Local Emergency Planning Committee if required",
  },
  {
    activityType: ActivityType.Lob,
    activityDescription: "Mail to FD",
    activityDisplayName: "Mailing report to Fire District if required",
  },
  {
    activityType: ActivityType.SendEmail,
    activityDescription: "LEPC/FD",
    activityDisplayName: "Emailing report to local agencies if required",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Birmingham Fire Department Questionaire",
    activityDisplayName: "Completing Birmingham Fire Department Questionnaire",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Morgan County LEPC Questionaire",
    activityDisplayName:
      "Completing Morgan County Local Emergency Planning Committee Questionnaire",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Arkansas Tier2 Submit",
    activityDisplayName: "Submitting report to state portal and paying fee",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Arkansas Portal",
    activityDisplayName: "Submitting report to state portal and paying fee",
  },
  {
    activityType: ActivityType.CreateZip,
    activityDescription: "Bulk Mail FD",
    activityDisplayName: null,
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Bulk Mail FD",
    activityDisplayName: null,
  },
  {
    activityType: ActivityType.Lob,
    activityDescription: "Bulk Mail FD",
    activityDisplayName: null,
  },
  {
    activityType: ActivityType.SendEmail,
    activityDescription: "FD Bulk",
    activityDisplayName: null,
  },
  {
    activityType: ActivityType.CreateZip,
    activityDescription: "Bulk Mail LEPC",
    activityDisplayName: null,
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Bulk Mail LEPC",
    activityDisplayName: null,
  },
  {
    activityType: ActivityType.Lob,
    activityDescription: "Bulk Mail LEPC",
    activityDisplayName: null,
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Arizona Portal",
    activityDisplayName: "Submitting report to state portal and paying fee",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Arizona - Send to Customer to Certify",
    activityDisplayName: "Waiting for customer to certify report in state portal",
  },
  {
    activityType: ActivityType.WaitForAllFD,
    activityDescription: "",
    activityDisplayName:
      "Submitting all reports in this jurisdiction to the Fire District together",
  },
  {
    activityType: ActivityType.WaitForAllLEPC,
    activityDescription: "",
    activityDisplayName:
      "Submitting all reports in this jurisdiction to the Local Emergency Planning Committee together",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "California Portal",
    activityDisplayName: "Submitting report to state portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Colorado Tier2 Submit",
    activityDisplayName: "Submitting report to state portal and paying fee",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Colorado Tier2 Submit - Revision",
    activityDisplayName: "Submitting report to state portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Colorado Springs Portal",
    activityDisplayName: "Submitting report to Colorado Springs portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Connecticut Portal",
    activityDisplayName: "Submitting report to state portal",
  },
  {
    activityType: ActivityType.FillPdf,
    activityDescription: "Sign Report",
    activityDisplayName: null,
  },
  {
    activityType: ActivityType.SendEmail,
    activityDescription: "LEPC",
    activityDisplayName: "Emailing report to Local Emergency Planning Committee if required",
  },
  {
    activityType: ActivityType.SendEmail,
    activityDescription: "FD",
    activityDisplayName: "Email Report to Fire Department if required",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Delaware Portal",
    activityDisplayName: "Submitting report to state portal and paying fee",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Delaware Portal - Revision",
    activityDisplayName: "Submitting report to state portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Florida ePlan",
    activityDisplayName: "Submitting report to state portal and paying fee",
  },
  {
    activityType: ActivityType.CreatePdf,
    activityDescription: "",
    activityDisplayName: null,
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Hawaii Tier2 Submit",
    activityDisplayName: "Submitting report to state portal and paying fee",
  },
  {
    activityType: ActivityType.SendEmail,
    activityDescription: "SERC",
    activityDisplayName: "Emailing report to State Emergency Response Commission if required",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Illinois Portal",
    activityDisplayName: "Submitting report to state portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Chicago Portal",
    activityDisplayName: "Submitting report to Chicago portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Indiana Portal",
    activityDisplayName: "Submitting report to state portal",
  },
  {
    activityType: ActivityType.WaitForAllCredentialReports,
    activityDescription: "",
    activityDisplayName: "Submitting fee payment for all reports under [username] credential",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Indiana Invoice - Once Per Credential",
    activityDisplayName: null,
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Kansas Portal",
    activityDisplayName: "Submitting report to state portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Ford County LEPC",
    activityDisplayName: "Submitting report to Ford County Local Emergency Planning Committee",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Lenexa Fire Dept Portal",
    activityDisplayName: "Submitting report to Lenexa Fire Department portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Kansas Invoice - Once Per Credential",
    activityDisplayName: null,
  },
  {
    activityType: ActivityType.Lob,
    activityDescription: "Check",
    activityDisplayName: null,
  },
  {
    activityType: ActivityType.SendEmail,
    activityDescription: "LEPC Bulk",
    activityDisplayName: null,
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Kentucky Portal",
    activityDisplayName: "Submitting report to state portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Kentucky Invoice - Once Per Credential",
    activityDisplayName: null,
  },
  {
    activityType: ActivityType.Geppetto,
    activityDescription: "downloadReport",
    activityDisplayName: null,
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "East Baton Rouge Portal",
    activityDisplayName: "Submitting report to East Baton Rouge portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Louisiana Invoice - Once Per Credential",
    activityDisplayName: null,
  },
  {
    activityType: ActivityType.Lob,
    activityDescription: "Mail to SERC",
    activityDisplayName: null,
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Massachusetts Portal",
    activityDisplayName: "Submitting report to state portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Maryland Portal",
    activityDisplayName: "Submitting report to state portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Howard County Portal",
    activityDisplayName: "Submitting report to Howard County portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Montgomery County Portal",
    activityDisplayName: "Submitting report to Montgomery County portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Maryland Invoice - Once Per Credential",
    activityDisplayName: null,
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Maine Certification Letter - Subject to Emergency Planning",
    activityDisplayName: "Preparing payment and documentation",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Michigan Portal",
    activityDisplayName: "Submitting report to state portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Minnesota Portal",
    activityDisplayName: "Submitting report to state portal and paying fee",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Minnesota Portal - Revision",
    activityDisplayName: "Submitting report to state portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Missouri Portal",
    activityDisplayName: "Submitting report to state portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "North Carolina ePlan",
    activityDisplayName: "Submitting report to state portal and paying fee",
  },
  {
    activityType: ActivityType.SignReportPages,
    activityDescription: "Sign Report PDF",
    activityDisplayName: null,
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Alamance County Portal",
    activityDisplayName: "Submitting report to Alamance County portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Mecklenburg County LEPC",
    activityDisplayName:
      "Submitting report to Mecklenburg County Local Emergency Planning Committee",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "North Dakota Portal",
    activityDisplayName: "Submitting report to state portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "North Dakota Invoice - Once Per Credential",
    activityDisplayName: null,
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Nebraska Portal",
    activityDisplayName: "Submitting report to state portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "New Hampshire Tier2 Submit",
    activityDisplayName: "Submitting report to state portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "New Jersey Portal",
    activityDisplayName: "Submitting report to state portal",
  },
  {
    activityType: ActivityType.Lob,
    activityDescription: "Mail to Lead Agency",
    activityDisplayName: "Mailing report to Lead Agency if required",
  },
  {
    activityType: ActivityType.Lob,
    activityDescription: "Mail to PD",
    activityDisplayName: "Mailing report to PD if required",
  },
  {
    activityType: ActivityType.SendEmail,
    activityDescription: "Lead Agency",
    activityDisplayName: "Emailing report to Lead Agency if required",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Howell Township Form and Fees",
    activityDisplayName: "Submitting Howell Township form and fees",
  },
  {
    activityType: ActivityType.WaitForAllStateReports,
    activityDescription: "",
    activityDisplayName: "Submitting payment for all reports in that jurisdiction",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "New Mexico Cover Letter - Once Per Organization",
    activityDisplayName: null,
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Nevada Portal",
    activityDisplayName: "Submitting report to state portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Nevada Invoice - Once Per Credential",
    activityDisplayName: null,
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "New York City Portal",
    activityDisplayName: "Submitting report to New York City portal",
  },
  {
    activityType: ActivityType.Tier2Submit,
    activityDescription: "Batch",
    activityDisplayName: null,
  },
  {
    activityType: ActivityType.CreateZip,
    activityDescription: "Certification Letters",
    activityDisplayName: null,
  },
  {
    activityType: ActivityType.AddDocument,
    activityDescription: "OH Certification Form",
    activityDisplayName: "Preparing Ohio Certification Form",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Fee Worksheet",
    activityDisplayName: "Submitting fee payment",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Cleveland Fire Department",
    activityDisplayName: "Submitting report to Cleveland Fire Department",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Warren County Portal",
    activityDisplayName: "Submitting report to Warren County portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "City of Cleveland",
    activityDisplayName: "Submitting report to City of Cleveland",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Oklahoma Tier2 Submit - Once Per Credential",
    activityDisplayName: null,
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Oregon Portal",
    activityDisplayName: "Submitting report to state portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Pennsylvania Portal",
    activityDisplayName: "Submitting report to state portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Pennsylvania Portal - Revision",
    activityDisplayName: "Submitting report to state portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Berks County Portal",
    activityDisplayName: "Submitting report to Berks County portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Montgomery, PA County Portal",
    activityDisplayName: "Submitting report to Montgomery County portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Complete Washington County Invoice",
    activityDisplayName: "Preparing payment to Washington County LEPC",
  },
  {
    activityType: ActivityType.Lob,
    activityDescription: "Check to Washington County LEPC",
    activityDisplayName: "Submitting payment to Washington County LEPC",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Complete Potter County Invoice",
    activityDisplayName: "Preparing payment to Potter County LEPC",
  },
  {
    activityType: ActivityType.Lob,
    activityDescription: "Check to Potter County LEPC",
    activityDisplayName: "Submitting payment to Potter County LEPC",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Complete Centre County Invoice",
    activityDisplayName: "Preparing payment to Centre County LEPC",
  },
  {
    activityType: ActivityType.Lob,
    activityDescription: "Check to Centre County LEPC",
    activityDisplayName: "Submitting payment to Centre County LEPC",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Complete Crawford County Invoice",
    activityDisplayName: "Preparing payment to Crawford County LEPC",
  },
  {
    activityType: ActivityType.Lob,
    activityDescription: "Check to Crawford County LEPC",
    activityDisplayName: "Submitting payment to Crawford County LEPC",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Puerto Rico Tier2 Submit",
    activityDisplayName: "Submitting report to state portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Richland County Form and Fees",
    activityDisplayName: "Submitting Richland County Form and fees",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "South Dakota Portal",
    activityDisplayName: "Submitting report to state portal",
  },
  {
    activityType: ActivityType.WaitForAllCNReports,
    activityDescription: "",
    activityDisplayName:
      "Submitting all reports under [CN] Customer Number to state portal and paying fees",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Plano Fire-Rescue Portal",
    activityDisplayName: "Submitting report to Plano Fire-Rescue portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Texas Portal - Once Per CN",
    activityDisplayName: null,
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Utah Tier2 Submit",
    activityDisplayName: "Submitting report to state portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Tooele County Portal",
    activityDisplayName: "Submitting report to Tooele County portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Utah County Portal",
    activityDisplayName: "Submitting report to Utah County portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Davis County Portal",
    activityDisplayName: "Submitting report to Davis County portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Salt Lake City Portal",
    activityDisplayName: "Submitting report to Salt Lake City portal",
  },
  {
    activityType: ActivityType.AddDocument,
    activityDescription: "VA Certification Form",
    activityDisplayName: "Completing VA Certification form",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Caroline County LEPC Portal",
    activityDisplayName:
      "Submitting report to Caroline County Local Emergency Planning Committee portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "City of Fredericksburg LEPC Portal",
    activityDisplayName:
      "Submitting report to City of Fredericksburg Local Emergency Planning Committee portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "City of Richmond Portal",
    activityDisplayName: "Submitting report to City of Richmond portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Fairfax County Portal",
    activityDisplayName: "Submitting report to Fairfax County portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Henrico County Portal",
    activityDisplayName: "Submitting report to Henrico County portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "King George County LEPC Portal",
    activityDisplayName:
      "Submitting report to King George County Local Emergency Planning Committee portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Roanoke Valley Joint LEPC Portal",
    activityDisplayName:
      "Submitting report to Roanoke Valley Joint Local Emergency Planning Committee portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Spotsylvania County LEPC Portal",
    activityDisplayName:
      "Submitting report to Spotsylvania County Local Emergency Planning Committee portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Stafford County LEPC Portal",
    activityDisplayName:
      "Submitting report to Stafford County Local Emergency Planning Committee portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "City of Lynchburg Portal",
    activityDisplayName: "Submitting report to City of Lynchburg portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Mecklenburg County LEPC Portal",
    activityDisplayName:
      "Submitting report to Mecklenburg County Local Emergency Planning Committee portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Newport News Fire Department Portal",
    activityDisplayName: "Submitting report to Newport News Fire Department portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Vermont Tier2 Submit",
    activityDisplayName: "Submitting report to state portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Washington Portal",
    activityDisplayName: "Submitting report to state portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Wisconsin Portal",
    activityDisplayName: "Submitting report to state portal and paying fee",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Wisconsin Portal - Revision",
    activityDisplayName: "Submitting report to state portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "West Virginia Portal",
    activityDisplayName: "Submitting report to state portal and paying fee",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "West Virginia Portal - Oil and Gas",
    activityDisplayName: "Submitting report to state portal",
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "West Virginia Invoice - Once Per Credential",
    activityDisplayName: null,
  },
  {
    activityType: ActivityType.Staff,
    activityDescription: "Wyoming Tier2 Submit",
    activityDisplayName: "Submitting report to state portal",
  },
];

const ACTIVITY_DISPLAY_STATE_OVERRIDES: Array<{ state: string } & ActivityDisplayConfig> = [
  {
    state: "AL",
    activityType: ActivityType.Lob,
    activityDescription: "Mail to FD",
    activityDisplayName: "Mailing report to fire department if required",
  },
  {
    state: "KS",
    activityType: ActivityType.WaitForAllFD,
    activityDescription: "",
    activityDisplayName:
      "Submitting all reports in this jurisdiction to the Local Emergency Planning Committee together",
  },
  {
    state: "LA",
    activityType: ActivityType.WaitForAllFD,
    activityDescription: "",
    activityDisplayName:
      "Submitting all reports in this jurisdiction to the Local Emergency Planning Committee together",
  },
  {
    state: "MT",
    activityType: ActivityType.WaitForAllFD,
    activityDescription: "",
    activityDisplayName:
      "Submitting all reports in this jurisdiction to the Local Emergency Planning Committee together",
  },
  {
    state: "KS",
    activityType: ActivityType.WaitForAllLEPC,
    activityDescription: "",
    activityDisplayName:
      "Submitting all reports in this jurisdiction to the Fire District together",
  },
  {
    state: "LA",
    activityType: ActivityType.WaitForAllLEPC,
    activityDescription: "",
    activityDisplayName:
      "Submitting all reports in this jurisdiction to the Fire District together",
  },
  {
    state: "MT",
    activityType: ActivityType.WaitForAllLEPC,
    activityDescription: "",
    activityDisplayName:
      "Submitting all reports in this jurisdiction to the Fire District together",
  },
  {
    state: "OH",
    activityType: ActivityType.SendEmail,
    activityDescription: "SERC",
    activityDisplayName: null,
  },
  {
    state: "VA",
    activityType: ActivityType.SendEmail,
    activityDescription: "SERC",
    activityDisplayName: "Emailing report and certification to State Emergency Response Commission",
  },
  {
    state: "LA",
    activityType: ActivityType.WaitForAllCredentialReports,
    activityDescription: "",
    activityDisplayName: "Submitting all reports under [username] credential and paying fees",
  },
  {
    state: "OK",
    activityType: ActivityType.WaitForAllCredentialReports,
    activityDescription: "",
    activityDisplayName:
      "Submitting all reports for this jurisdiction to the state in a single T2S file and paying fees",
  },
  {
    state: "KS",
    activityType: ActivityType.Lob,
    activityDescription: "Check",
    activityDisplayName: null,
  },
  {
    state: "MD",
    activityType: ActivityType.Lob,
    activityDescription: "Check",
    activityDisplayName: null,
  },
  {
    state: "ME",
    activityType: ActivityType.Lob,
    activityDescription: "Check",
    activityDisplayName: "Mailing check for fee payment to state if required",
  },
  {
    state: "NM",
    activityType: ActivityType.Lob,
    activityDescription: "Check",
    activityDisplayName: null,
  },
  {
    state: "PA",
    activityType: ActivityType.Lob,
    activityDescription: "Check",
    activityDisplayName: "Mailing fee payment and certification letter to state if required",
  },
  {
    state: "OH",
    activityType: ActivityType.WaitForAllStateReports,
    activityDescription: "",
    activityDisplayName: "Submitting all reports in this jurisdiction to the state together",
  },
  {
    state: "OH",
    activityType: ActivityType.Staff,
    activityDescription: "City of Wadsworth",
    activityDisplayName: "Submitting report to City of Wadsworth",
  },
];

/** States where Geppetto shows "Submitting report to {state} portal and paying fee" */
const GEPPETTO_FEE_STATES: readonly string[] = ["AK", "AL", "GA", "MS", "NY", "TN"];

/** States where Geppetto shows "Submitting report to {state} portal" (no fee) */
const GEPPETTO_NOFEE_STATES: readonly string[] = ["IA", "MT", "SC"];

function getGeppettoVariant(state: string): "fee" | "nofee" | null {
  if (GEPPETTO_FEE_STATES.includes(state)) return "fee";
  if (GEPPETTO_NOFEE_STATES.includes(state)) return "nofee";
  return null;
}

/**
 * Build flat map (type + description → config) and state overrides from deduplicated config.
 */
function buildMapsFromDedupedConfigs(
  configs: ActivityDisplayConfig[],
  stateOverridesConfig: Array<{ state: string } & ActivityDisplayConfig>
): {
  byTypeDesc: Map<string, ActivityDisplayConfig>;
  stateOverrides: Map<string, ActivityDisplayConfig>;
} {
  const byTypeDesc = new Map<string, ActivityDisplayConfig>();
  for (const c of configs) {
    byTypeDesc.set(getTypeDescriptionKey(c.activityType, c.activityDescription), c);
  }
  const stateOverrides = new Map<string, ActivityDisplayConfig>();
  for (const o of stateOverridesConfig) {
    stateOverrides.set(
      `${o.state}-${getTypeDescriptionKey(o.activityType, o.activityDescription)}`,
      o
    );
  }
  return {
    byTypeDesc,
    stateOverrides,
  };
}

const { byTypeDesc: activityDisplayByTypeDesc, stateOverrides: activityDisplayStateOverrides } =
  buildMapsFromDedupedConfigs(ACTIVITY_DISPLAY_CONFIGS, ACTIVITY_DISPLAY_STATE_OVERRIDES);
